/**
 * Status Utilities
 * 
 * This module provides centralized status management functions for the application.
 * It includes color mapping, status options, and unified status handling for
 * different types of statuses (submissions, part vector searches, dataset processing).
 * 
 * All status-related functions are centralized here to ensure consistency
 * across the application and make maintenance easier.
 */

/**
 * Gets the color for a submission status tag
 * @param {string} status - The submission status
 * @returns {string} The color for the status tag
 */
export const getSubmissionStatusColor = (status) => {
  switch (status) {
    case "submitted":
      return "blue";
    case "accepted":
      return "green";
    case "printed":
      return "purple";
    case "scan_uploaded":
      return "cyan";
    default:
      return "default";
  }
};

/**
 * Gets the color for a part vector search status tag
 * @param {string} status - The part vector search status
 * @returns {string} The color for the status tag
 */
export const getPartVectorSearchStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "green";
    case "processing":
      return "blue";
    case "pending":
      return "orange";
    case "failed":
      return "red";
    case "started":
      return "cyan";
    default:
      return "default";
  }
};

/**
 * Gets the appropriate color for any status based on status type
 * @param {string} status - The status value
 * @param {string} statusType - The type of status ('submission' or 'partVectorSearch')
 * @returns {string} The color for the status tag
 */
export const getStatusColor = (status, statusType = 'submission') => {
  if (statusType === 'partVectorSearch') {
    return getPartVectorSearchStatusColor(status);
  }
  return getSubmissionStatusColor(status);
};

/**
 * Generates dropdown options for submission status based on the model type definition
 * @returns {Array<{value: string, label: string}>}
 */
export const getSubmissionStatusOptions = () => {
  return [
    { value: "submitted", label: "Submitted" },
    { value: "accepted", label: "Accepted" },
    { value: "printed", label: "Printed" },
    { value: "scan_uploaded", label: "Scan Uploaded" },
  ];
};

/**
 * Gets the color for a dataset processing status
 * @param {string} status - The dataset processing status
 * @returns {string} The color for the status
 */
export const getDatasetProcessingStatusColor = (status) => {
  switch (status) {
    case "processing":
    case "submitted":
      return "blue";
    case "completed":
    case "complete":
    case "success":
      return "green";
    case "error":
    case "failed":
      return "red";
    default:
      return "black";
  }
}; 